import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/apiClient.js';
import { useCurrentUser } from './CurrentUserContext.jsx';
import { useCopilotController } from './CopilotController.jsx';
import { IconClose, IconSparkles } from '../components/icons.jsx';
import { AiGroundingChip } from '../components/inspectors/AiGroundingChip.jsx';
import { buildRouteContext } from '../lib/routeContext.js';
import { personaKeyFromRoles, primaryRoleLabel } from '../lib/personaKey.js';

const STARTERS_BY_PERSONA = {
  'Executive Sponsor': [
    'Summarize the top three portfolio risks driving red status today.',
    'What is the Gate 3 readiness status for Sentinel Guard?',
    'Which AI runs are pending my review?',
  ],
  'Portfolio Leader': [
    'Which sprint blockers threaten the AI Diagnostic IOC?',
    'Show forecast variance by portfolio.',
    'Which projects have unresolved lineage conflicts?',
  ],
  'Data Steward': [
    'Which connectors failed their last sync?',
    'Where do PMRT and warehouse forecasts disagree?',
    'List recent manual overrides.',
  ],
  default: [
    'What is the current state of the portfolio?',
    'Show me the most recent AI briefings.',
    'Which access requests need review?',
  ],
};

export function AiCopilotDrawer({ open, onClose, onResizeStart, resizing }) {
  const currentUser = useCurrentUser();
  const { user, roles = [] } = currentUser;
  const location = useLocation();
  const navigate = useNavigate();
  // Roles first, title-string only as a legacy fallback (DEF-002).
  const personaBucket = roles.length ? personaKeyFromRoles(roles) : null;
  const personaKey = personaBucket
    ? {
        executive_sponsor: 'Executive Sponsor',
        delivery_leader: 'Portfolio Leader',
        data_steward: 'Data Steward',
      }[personaBucket] || 'default'
    : (user?.title || '').includes('Executive')
      ? 'Executive Sponsor'
      : (user?.title || '').includes('Portfolio')
        ? 'Portfolio Leader'
        : (user?.title || '').includes('Steward')
          ? 'Data Steward'
          : 'default';
  const starterLabel = primaryRoleLabel(roles) || (personaKey !== 'default' ? personaKey : null);
  const starters = STARTERS_BY_PERSONA[personaKey] || STARTERS_BY_PERSONA.default;
  const [thread, setThread] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  // Feature C — "Explain this chart" prefill channel. A page opens the drawer
  // via the CopilotController with a nonce-tagged prefill { question, visual,
  // autoSubmit }. When the nonce changes we set the input to the question,
  // attach the chart snapshot to the NEXT ask context, and (when autoSubmit)
  // send immediately. Refs keep the effect dependency-clean and let the visual
  // ride exactly one ask.
  const { prefill } = useCopilotController();
  const attachedVisualRef = useRef(null);
  const lastPrefillNonceRef = useRef(0);
  const askRef = useRef(null);

  useEffect(() => {
    if (open && endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [open, thread.length]);

  async function ask(q, visualArg) {
    const question = (q ?? input).trim();
    if (!question) return;
    setInput('');
    const routeContext = buildRouteContext({ location, currentUser });
    // Attach the chart snapshot to this one ask: an explicit arg wins, else the
    // pending prefill visual. Clear it after so it never leaks into a later,
    // unrelated question.
    const visual = visualArg !== undefined ? visualArg : attachedVisualRef.current;
    attachedVisualRef.current = null;
    const context = visual ? { ...routeContext, visual } : routeContext;
    setThread((t) => [...t, { role: 'user', text: question }]);
    setBusy(true);
    try {
      const res = await api.post('/ai/ask', { question, context });
      const data = res.data || res;
      setThread((t) => [
        ...t,
        {
          role: 'assistant',
          text: data.answer || '(no answer)',
          mode: data.mode,
          citations: data.citations || [],
          modelId: data.modelId,
          groundedIn: data.groundedIn || null,
        },
      ]);
    } catch (err) {
      setThread((t) => [...t, { role: 'assistant', text: `Sorry — ${err.message}`, error: true }]);
    } finally {
      setBusy(false);
    }
  }

  // Keep a stable handle to the latest `ask` so the prefill effect can call it
  // without listing the re-created function in its dependency array.
  askRef.current = ask;

  // Respond to a new prefill (nonce bump): populate the input, stage the chart
  // snapshot for the next ask, and auto-submit when the caller requested it.
  // Prefill-only mode (autoSubmit:false) leaves the send to the user.
  useEffect(() => {
    if (!prefill || prefill.nonce === lastPrefillNonceRef.current) return;
    lastPrefillNonceRef.current = prefill.nonce;
    setInput(prefill.question || '');
    attachedVisualRef.current = prefill.visual || null;
    if (prefill.autoSubmit) {
      askRef.current(prefill.question, prefill.visual || null);
    }
  }, [prefill]);

  return (
    <aside
      className={`ai-copilot-drawer ${open ? 'open' : ''} ${resizing ? 'resizing' : ''}`}
      data-testid="prime-copilot-sidebar"
      aria-hidden={!open}
      {...(!open ? { inert: '' } : {})}
    >
      {onResizeStart && open ? (
        <div
          className="copilot-resize-handle"
          data-testid="copilot-resize-handle"
          onMouseDown={onResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize copilot drawer"
          title="Drag to resize"
        />
      ) : null}
      <header className="copilot-header">
        <div className="row">
          <IconSparkles size={18} />
          <strong>PRIME Copilot</strong>
        </div>
        <button className="topbar-btn" onClick={onClose} aria-label="Close copilot">
          <IconClose size={18} />
        </button>
      </header>
      <div className="copilot-modechip">
        {thread.length === 0 ? (
          <span className="chip outline">
            Pending human review. Grounded answers cite source artifacts.
          </span>
        ) : null}
      </div>
      <div className="copilot-thread" aria-live="polite">
        {thread.length === 0 && (
          <div className="copilot-starters">
            <p className="eyebrow">
              {starterLabel ? `Suggested for ${starterLabel}` : 'Suggested questions'}
            </p>
            {starters.map((s) => (
              <button key={s} className="btn ghost sm copilot-starter" onClick={() => ask(s)}>
                {s}
              </button>
            ))}
            {/* Help & tours (WP5 SME onboarding): quick paths out of a blank
                thread to the help hub, the orientation tour, and the glossary.
                Each closes the drawer so focus returns to the destination. */}
            <div className="copilot-help-block" data-testid="copilot-help-block">
              <p className="eyebrow">Help &amp; tours</p>
              <button
                type="button"
                className="copilot-help-link"
                data-testid="copilot-help-getting-started"
                onClick={() => {
                  navigate('/help');
                  onClose?.();
                }}
              >
                Help &amp; getting started
              </button>
              <button
                type="button"
                className="copilot-help-link"
                data-testid="copilot-help-tour"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                      new CustomEvent('prime:start-tour', { detail: { tourId: 'orientation' } }),
                    );
                  }
                  onClose?.();
                }}
              >
                Take the 2-minute tour
              </button>
              <button
                type="button"
                className="copilot-help-link"
                data-testid="copilot-help-glossary"
                onClick={() => {
                  navigate('/help/glossary');
                  onClose?.();
                }}
              >
                Glossary
              </button>
            </div>
          </div>
        )}
        {thread.map((msg, i) => (
          <div className={`copilot-msg ${msg.role}`} key={i}>
            {msg.role === 'assistant' && msg.groundedIn ? (
              <AiGroundingChip groundedIn={msg.groundedIn} />
            ) : null}
            <div className="copilot-msg-text">{msg.text}</div>
            {msg.citations?.length ? (
              <div className="copilot-citations">
                <p className="eyebrow">Citations</p>
                <ol>
                  {msg.citations.map((c, idx) => (
                    <li key={c.citationId || idx}>
                      <strong>{c.source}</strong>
                      {c.score != null ? (
                        <span className="subtle"> · {Number(c.score).toFixed(2)}</span>
                      ) : null}
                      <div className="muted">{c.excerpt}</div>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form
        className="copilot-input"
        onSubmit={(e) => {
          e.preventDefault();
          ask();
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask PRIME…"
          aria-label="Ask PRIME"
          disabled={busy}
        />
        <button className="btn primary sm" disabled={busy || !input.trim()}>
          {busy ? '…' : 'Ask'}
        </button>
      </form>
    </aside>
  );
}
